import { Event, NestedEvent, NestedPlayer, Player } from "@/types/serializers"
import { map } from "lodash-es"
import Combobox from "./Combobox"
import Button from "./Button"
import { ArrowLongRightIcon } from "@heroicons/react/24/outline"

type Props = {
  players: NestedPlayer[]
  events: NestedEvent[]
  onNext: () => void
  previous: () => void
  newPlayers: any[]
  newEvents: any[]
  playerMapping: Record<string, string | undefined>
  eventMapping: Record<string, string | undefined>
  onMatch: (
    type: "event" | "player",
    match: {
      source: string
      target: string | undefined
    }
  ) => void
}

const GameUploadMatch = ({
  players,
  events,
  onNext,
  previous,
  newPlayers,
  playerMapping,
  newEvents,
  eventMapping,
  onMatch,
}: Props) => {
  const handleEventChange = (
    sourceName: string,
    targetName: string | undefined
  ) =>
    onMatch("event", {
      source: sourceName,
      target: targetName,
    })

  const handlePlayerChange = (
    sourceName: string,
    targetName: string | undefined
  ) => {
    onMatch("player", {
      source: sourceName,
      target: targetName,
    })
  }

  const renderEvents = () => (
    <div className="space-y-3">
      {newEvents.map((event: Event) => (
        <div
          key={event.name}
          className="grid grid-cols-[120px_35px_1fr] items-center sm:grid-cols-[1fr_50px_1fr]"
        >
          <div className="line-clamp-1 text-right text-sm leading-none text-gray-900">
            {event.name}
          </div>

          <div className="flex items-center justify-center">
            <ArrowLongRightIcon className="text-chessdrop-600 h-5 w-5" />
          </div>

          <div>
            <Combobox
              options={map(events, "name")}
              playerName={eventMapping[event.name]}
              placeholder="New event"
              label="Select or create event"
              onChange={handleEventChange.bind(null, event.name)}
            />
          </div>
        </div>
      ))}
    </div>
  )

  const renderPlayers = () => (
    <div className="space-y-3">
      {newPlayers.map((player: Player, index) => {
        const isLastHalf = index >= newPlayers.length / 2

        return (
          <div
            key={player.name}
            className="grid grid-cols-[120px_35px_1fr] items-center sm:grid-cols-[1fr_50px_1fr]"
          >
            <div className="line-clamp-1 text-right text-sm leading-none text-gray-900">
              {player.name}
            </div>

            <div className="flex items-center justify-center">
              <ArrowLongRightIcon className="text-chessdrop-600 h-5 w-5" />
            </div>

            <div>
              <Combobox
                options={map(players, "name")}
                playerName={playerMapping[player.name]}
                placeholder="New player"
                label="Select or create player"
                onChange={handlePlayerChange.bind(null, player.name)}
                alignOptions={isLastHalf ? "top" : "bottom"}
              />
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      <div className="mt-4 max-h-[600px] divide-y divide-gray-300 overflow-x-hidden overflow-y-auto rounded-lg border border-gray-300 bg-white px-2 sm:mt-6 sm:px-3">
        {newEvents.length > 0 && (
          <div className="py-4">
            <h3 className="mb-2 text-center font-semibold text-gray-700">
              Match events
            </h3>
            {renderEvents()}
          </div>
        )}

        {newPlayers.length > 0 && (
          <div className="py-4">
            <h3 className="mb-2 text-center font-semibold text-gray-700">
              Match players
            </h3>
            {renderPlayers()}
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 items-center gap-2.5 sm:mt-6">
        <Button fullWidth style="secondary" onClick={previous}>
          Back
        </Button>
        <Button fullWidth onClick={onNext} className="col-span-2">
          Import games
        </Button>
      </div>
    </>
  )
}

export default GameUploadMatch
